'use client';

import { useCallback, useRef, useState } from 'react';
import { useWatch, UseFormReturn } from 'react-hook-form';

import {
  getOrCreateShuffledOptions,
  useProjectResponseModeContext,
  type ResponderPanel,
} from '@/shared-survey';
import {
  type Channel,
  type GetProjectQuery,
  type MessageAssetInput,
} from '@/survey-graphql';

import { useSurveyAiFormsAudio } from './survey-ai-forms-audio.hooks';
import {
  type AiFormsResponseValues,
  normalizeFormValues,
  useFormResponses,
  useTranscriptManagement,
} from './survey-ai-forms-form.hooks';
import { useSurveyAiFormsSubmission } from './survey-ai-forms-submission.hooks';
import { useIsMobile } from './survey-ai-forms-viewport.hooks';
import {
  getNextUnansweredQuestionInSequence,
  getNextUnsubmittedQuestionInSequence,
} from './survey-ai-forms.helpers';
import type {
  FormAnswerValue,
  QuestionResponse,
  ResponseMode,
  ViewMode,
} from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface UseSurveyAiFormsProps {
  project: NonNullable<GetProjectQuery['project']>;
  form: UseFormReturn<AiFormsResponseValues>;
  sessionId: string;
  isPreviewMode?: boolean;
  channel?: Channel;
  panel?: ResponderPanel | null;
}

export function useSurveyAiForms({
  project,
  form,
  sessionId,
  isPreviewMode = false,
  channel,
  panel = null,
}: UseSurveyAiFormsProps): {
  displayProject: NonNullable<GetProjectQuery['project']>;
  questions: ProjectQuestion[];
  isMobile: boolean;
  responses: Map<string, QuestionResponse>;
  viewMode: ViewMode;
  responseMode: ResponseMode;
  currentQuestionId: string | null;
  showInstructions: boolean;
  currentQuestion: ProjectQuestion | null;
  currentQuestionIndex: number;
  progressPercentage: number;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  handleAiResponseSubmit: (value: FormAnswerValue) => void;
  handleNext: () => void;
  handleFormResponseChange: (
    questionId: string,
    value: FormAnswerValue
  ) => void;
  handleFormResponseSubmit: (value: FormAnswerValue) => void;
  handleQuestionFocus: (questionId: string) => void;
  handleQuestionNavigate: (questionId: string) => void;
  handleEnableAi: () => void;
  handleViewModeToggle: () => void;
  handleSubmit: () => void;
  handleFormSubmit: () => void;
  handleCloseInstructions: () => void;
  registerVoiceStopCallback: (callback: (() => void) | null) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  handleAddFormMessage: (messageData: {
    content: string;
    insightId: string;
    questionId: string;
    sessionId: string;
    transcript?: string;
    assets?: MessageAssetInput[];
  }) => Promise<Set<string> | null>;
  isSubmitting: boolean;
  isAddingInsight: boolean;
  isAddingMessage: boolean;
  isProcessingResponse: boolean;
  insightId: string | null;
  canSubmit: boolean;
  submittedQuestionIds: Set<string>;
  validationErrors: Set<string>;
  hasTriedSubmit: boolean;
  isAIFormAssistanceEnabled: boolean;
} {
  const isAIFormAssistanceEnabled = project.isAIFormAssistanceEnabled !== false;

  const activeVoiceStopCallbackRef = useRef<(() => void) | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>(
    isAIFormAssistanceEnabled ? 'ai' : 'form'
  );
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(
    project.questions?.[0]?.id || null
  );
  const [showInstructions, setShowInstructions] = useState(true);

  const isMobile = useIsMobile();

  const { responseMode, eventsObservable } = useProjectResponseModeContext();

  const audio = useSurveyAiFormsAudio({
    organizationId: project.organization?.id,
    anonymous: project?.anonymous,
  });

  const { control } = form;
  const formValues = useWatch({ control });

  const { transcripts, saveTranscript, getTranscript } =
    useTranscriptManagement(form);

  const displayProject = (() => {
    const questions = (project.questions ?? []).map((q) => {
      const rawOptions = q.options ?? [];
      const shuffled =
        getOrCreateShuffledOptions(
          sessionId,
          q.id,
          rawOptions,
          q.randomize ?? false
        ) ?? rawOptions;
      const options = shuffled
        .map((opt) => rawOptions.find((o) => o.value === opt.value))
        .filter((o): o is NonNullable<typeof o> => o != null);
      return Object.assign({}, q, {
        options: options.length ? options : rawOptions,
      });
    });
    return { ...project, questions };
  })();

  const questions = displayProject.questions;
  const projectId = displayProject.id;

  const responses = useFormResponses(normalizeFormValues(formValues));

  const navigateToQuestion = useCallback(
    (questionId: string | null) => {
      setCurrentQuestionId(questionId);
      eventsObservable.next({ type: 'question_changed', questionId });
    },
    [eventsObservable]
  );

  const submission = useSurveyAiFormsSubmission({
    project,
    questions,
    projectId,
    form,
    sessionId,
    isPreviewMode,
    viewMode,
    currentQuestionId,
    setCurrentQuestionId: navigateToQuestion,
    responses,
    transcripts,
    audio,
    channel,
    panel,
  });

  const currentQuestion =
    questions.find((q) => q.id === currentQuestionId) || null;

  const handleQuestionFocus = (questionId: string) => {
    navigateToQuestion(questionId);
    if (viewMode === 'ai') {
      setViewMode('form');
    }
  };

  const handleQuestionNavigate = (questionId: string) => {
    navigateToQuestion(questionId);
  };

  const registerVoiceStopCallback = (callback: (() => void) | null) => {
    activeVoiceStopCallbackRef.current = callback;
  };

  const handleEnableAi = () => {
    setViewMode('ai');
    const nextQuestion = getNextUnansweredQuestionInSequence(
      questions,
      responses,
      currentQuestionId
    );
    if (nextQuestion) {
      navigateToQuestion(nextQuestion.id);
    } else {
      navigateToQuestion(null);
    }
  };

  const handleViewModeToggle = () => {
    if (!isAIFormAssistanceEnabled) return;

    if (viewMode === 'form') {
      const nextUnsubmittedQuestion = getNextUnsubmittedQuestionInSequence(
        questions,
        submission.submittedQuestionIds,
        currentQuestionId
      );

      navigateToQuestion(nextUnsubmittedQuestion?.id ?? null);
      setViewMode('ai');
    } else {
      setViewMode('form');
    }
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };

  const currentQuestionIndex = questions.findIndex(
    (q) => q.id === currentQuestionId
  );

  const answeredQuestionsCount = Array.from(responses.values()).length;
  const progressPercentage =
    questions.length > 0
      ? Math.min((answeredQuestionsCount / questions.length) * 100, 100)
      : 0;

  const progress = Math.min(Math.max(progressPercentage / 100, 0), 1);

  const estimatedTotalTimeInSeconds = project.estimatedCompletionTime ?? 0;

  return {
    displayProject,
    questions,
    isMobile,
    responses,
    viewMode,
    responseMode,
    currentQuestionId,
    showInstructions,
    currentQuestion,
    currentQuestionIndex:
      currentQuestionIndex !== -1 ? currentQuestionIndex : 0,
    progressPercentage,
    progress,
    estimatedTotalTimeInSeconds,
    handleAiResponseSubmit: submission.handleAiResponseSubmit,
    handleNext: submission.handleNext,
    handleFormResponseChange: submission.handleFormResponseChange,
    handleFormResponseSubmit: submission.handleFormResponseSubmit,
    handleQuestionFocus,
    handleQuestionNavigate,
    handleEnableAi,
    handleViewModeToggle,
    handleSubmit: submission.handleSubmit,
    handleFormSubmit: submission.handleFormSubmit,
    handleCloseInstructions,
    registerVoiceStopCallback,
    saveTranscript,
    getTranscript,
    handleAddFormMessage: submission.handleAddFormMessage,
    isSubmitting: submission.isSubmitting,
    isAddingInsight: submission.isAddingInsight,
    isAddingMessage: submission.isAddingMessage,
    isProcessingResponse: submission.isProcessingResponse,
    insightId: submission.insightId,
    canSubmit: submission.canSubmit,
    submittedQuestionIds: submission.submittedQuestionIds,
    validationErrors: submission.validationErrors,
    hasTriedSubmit: submission.hasTriedSubmit,
    isAIFormAssistanceEnabled,
  };
}
